import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const METHODS = ["venmo", "paypal", "zelle", "cashapp", "store_credit", "manual"];

function monthRange() {
    const now = new Date();
    const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
    const end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 0));
    const iso = (d) => d.toISOString().slice(0, 10);
    return { from: iso(start), to: iso(end) };
}

function lastMonthRange() {
    const now = new Date();
    const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1));
    const end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 0));
    const iso = (d) => d.toISOString().slice(0, 10);
    return { from: iso(start), to: iso(end) };
}

function MarkPaidModal({ open, row, periodFrom, periodTo, password, onClose, onSaved }) {
    const [amount, setAmount] = useState("");
    const [method, setMethod] = useState("venmo");
    const [reference, setReference] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (open && row) {
            setAmount(String(row.balance_due > 0 ? row.balance_due : row.commission_owed));
            setReference(row.payout_handle || row.social_handle || "");
            setMethod(row.payout_method || "venmo");
        }
    }, [open, row]);

    if (!open || !row) return null;

    const submit = async (e) => {
        e.preventDefault();
        const amt = parseFloat(amount);
        if (!amt || amt <= 0) return toast.error("Amount must be > 0");
        setBusy(true);
        try {
            await axios.post(`${API}/admin/payouts/mark`, {
                ambassador_id: row.ambassador_id,
                period_start: periodFrom,
                period_end: periodTo,
                amount: amt,
                method,
                reference: reference.trim() || null,
            }, { headers: { "X-Admin-Password": password } });
            toast.success(`Marked $${amt.toFixed(2)} paid to ${row.name}`);
            onSaved();
            onClose();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to mark paid");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
            data-testid="payout-mark-modal"
        >
            <div className="bg-fgm-surface border border-white/10 w-full max-w-md">
                <div className="border-b border-white/10 px-5 py-3 flex items-center justify-between">
                    <h3 className="font-display tracking-[0.25em] uppercase text-white text-sm">Mark Paid · {row.name}</h3>
                    <button onClick={onClose} className="text-zinc-500 hover:text-white text-2xl leading-none" aria-label="Close">×</button>
                </div>
                <form onSubmit={submit} className="p-5 space-y-3">
                    <div className="text-xs text-zinc-400">
                        Period: <span className="font-mono text-white">{periodFrom}</span> → <span className="font-mono text-white">{periodTo}</span>
                    </div>
                    <div>
                        <label className="text-[10px] font-display tracking-[0.25em] uppercase text-zinc-500 block mb-1">Amount Paid (USD)</label>
                        <input
                            data-testid="payout-mark-amount"
                            type="number"
                            step="0.01"
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            required
                            className="w-full bg-black border border-white/15 px-3 py-2 text-sm text-white font-mono"
                        />
                        <p className="text-[10px] text-zinc-600 mt-1">
                            Commission owed: <span className="text-white font-mono">${row.commission_owed.toFixed(2)}</span>
                            {row.paid_in_range > 0 && <> · Already paid this period: <span className="text-white font-mono">${row.paid_in_range.toFixed(2)}</span></>}
                        </p>
                    </div>
                    <div>
                        <label className="text-[10px] font-display tracking-[0.25em] uppercase text-zinc-500 block mb-1">Method</label>
                        <select
                            data-testid="payout-mark-method"
                            value={method}
                            onChange={(e) => setMethod(e.target.value)}
                            className="w-full bg-black border border-white/15 px-3 py-2 text-sm text-white"
                        >
                            {METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="text-[10px] font-display tracking-[0.25em] uppercase text-zinc-500 block mb-1">Reference (handle / tx id / note)</label>
                        <input
                            data-testid="payout-mark-reference"
                            value={reference}
                            onChange={(e) => setReference(e.target.value)}
                            placeholder="@venmo-handle or tx ABC123"
                            className="w-full bg-black border border-white/15 px-3 py-2 text-sm text-white font-mono"
                        />
                    </div>
                    <div className="flex items-center justify-end gap-2 pt-2">
                        <button type="button" onClick={onClose} className="text-[10px] font-display tracking-[0.25em] uppercase px-4 py-2 border border-white/15 text-zinc-300 hover:text-white">Cancel</button>
                        <button
                            data-testid="payout-mark-submit"
                            type="submit"
                            disabled={busy}
                            className="text-[10px] font-display tracking-[0.25em] uppercase px-5 py-2 bg-[#E63946] text-white hover:bg-white hover:text-[#E63946] disabled:opacity-50"
                        >
                            {busy ? "Saving…" : "✓ Mark Paid"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default function PayoutsTab({ password }) {
    const tm = monthRange();
    const [periodFrom, setPeriodFrom] = useState(tm.from);
    const [periodTo, setPeriodTo] = useState(tm.to);
    const [report, setReport] = useState({ rows: [], totals: { orders: 0, gross_sales: 0, commission_owed: 0, paid_in_range: 0, balance_due: 0 } });
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalRow, setModalRow] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [rep, hist] = await Promise.all([
                axios.get(`${API}/admin/payouts/report?period_from=${periodFrom}&period_to=${periodTo}`, { headers: { "X-Admin-Password": password } }),
                axios.get(`${API}/admin/payouts/history`, { headers: { "X-Admin-Password": password } }),
            ]);
            setReport(rep.data);
            setHistory(hist.data || []);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to load");
        } finally {
            setLoading(false);
        }
    }, [periodFrom, periodTo, password]);

    useEffect(() => { load(); }, [load]);

    const setMonth = (which) => {
        const r = which === "this" ? monthRange() : lastMonthRange();
        setPeriodFrom(r.from);
        setPeriodTo(r.to);
    };

    const downloadCsv = async () => {
        try {
            const res = await axios.get(
                `${API}/admin/payouts/report.csv?period_from=${periodFrom}&period_to=${periodTo}`,
                { headers: { "X-Admin-Password": password }, responseType: "blob" }
            );
            const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = `fgm-payouts-${periodFrom}_to_${periodTo}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success("CSV downloaded");
        } catch (err) {
            toast.error("Download failed");
        }
    };

    const removePayout = async (p) => {
        if (!window.confirm(`Undo payout of $${p.amount} to ${p.ambassador_name}?`)) return;
        try {
            await axios.delete(`${API}/admin/payouts/${p.id}`, { headers: { "X-Admin-Password": password } });
            toast.success("Payout undone");
            load();
        } catch (err) {
            toast.error("Undo failed");
        }
    };

    const totals = report.totals || {};
    const owedRows = useMemo(() => (report.rows || []).filter((r) => r.commission_owed > 0), [report.rows]);

    return (
        <div data-testid="payouts-tab" className="space-y-5">
            {/* Period selector + actions */}
            <div className="bg-fgm-surface border border-white/10 p-5">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                    <h3 className="font-display tracking-[0.3em] text-sm uppercase text-zinc-400">Commission Payout Report</h3>
                    <div className="flex items-center gap-2">
                        <button data-testid="payouts-period-this" onClick={() => setMonth("this")} className="text-[10px] font-display tracking-[0.25em] uppercase px-3 py-1.5 border border-white/15 text-zinc-300 hover:text-white">This Month</button>
                        <button data-testid="payouts-period-last" onClick={() => setMonth("last")} className="text-[10px] font-display tracking-[0.25em] uppercase px-3 py-1.5 border border-white/15 text-zinc-300 hover:text-white">Last Month</button>
                    </div>
                </div>
                <div className="flex flex-wrap items-end gap-3">
                    <div>
                        <label className="text-[10px] font-display tracking-[0.25em] uppercase text-zinc-500 block mb-1">From</label>
                        <input
                            data-testid="payouts-from"
                            type="date"
                            value={periodFrom}
                            onChange={(e) => setPeriodFrom(e.target.value)}
                            className="bg-black border border-white/15 px-3 py-2 text-sm text-white font-mono"
                        />
                    </div>
                    <div>
                        <label className="text-[10px] font-display tracking-[0.25em] uppercase text-zinc-500 block mb-1">To</label>
                        <input
                            data-testid="payouts-to"
                            type="date"
                            value={periodTo}
                            onChange={(e) => setPeriodTo(e.target.value)}
                            className="bg-black border border-white/15 px-3 py-2 text-sm text-white font-mono"
                        />
                    </div>
                    <button
                        data-testid="payouts-csv"
                        onClick={downloadCsv}
                        className="text-[10px] font-display tracking-[0.25em] uppercase px-4 py-2 border border-[#E63946] text-[#E63946] hover:bg-[#E63946] hover:text-white"
                    >
                        ↓ Download CSV
                    </button>
                </div>

                {/* KPI strip */}
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mt-5 border-t border-white/5 pt-4">
                    {[
                        { label: "Orders", value: totals.orders ?? 0, mono: true },
                        { label: "Gross", value: `$${(totals.gross_sales ?? 0).toFixed(2)}`, mono: true },
                        { label: `Owed`, value: `$${(totals.commission_owed ?? 0).toFixed(2)}`, mono: true },
                        { label: "Paid", value: `$${(totals.paid_in_range ?? 0).toFixed(2)}`, mono: true },
                        { label: "Balance Due", value: `$${(totals.balance_due ?? 0).toFixed(2)}`, mono: true, highlight: true },
                    ].map((k, i) => (
                        <div key={i} className="text-center">
                            <div className="text-[10px] font-display tracking-[0.3em] uppercase text-zinc-500">{k.label}</div>
                            <div className={`mt-1 ${k.mono ? "font-mono" : "font-display"} text-xl ${k.highlight ? "text-[#E63946] font-bold" : "text-white"}`}>
                                {k.value}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Per-ambassador table */}
            <div className="bg-fgm-surface border border-white/10 overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-black/40 border-b border-white/10">
                        <tr>
                            <th className="text-left px-4 py-3 font-display tracking-[0.2em] text-xs text-zinc-400 uppercase">Ambassador</th>
                            <th className="text-right px-4 py-3 font-display tracking-[0.2em] text-xs text-zinc-400 uppercase">Rank</th>
                            <th className="text-right px-4 py-3 font-display tracking-[0.2em] text-xs text-zinc-400 uppercase">Rate</th>
                            <th className="text-right px-4 py-3 font-display tracking-[0.2em] text-xs text-zinc-400 uppercase">Orders</th>
                            <th className="text-right px-4 py-3 font-display tracking-[0.2em] text-xs text-zinc-400 uppercase">Gross</th>
                            <th className="text-right px-4 py-3 font-display tracking-[0.2em] text-xs text-zinc-400 uppercase">Owed</th>
                            <th className="text-right px-4 py-3 font-display tracking-[0.2em] text-xs text-zinc-400 uppercase">Paid</th>
                            <th className="text-right px-4 py-3 font-display tracking-[0.2em] text-xs text-zinc-400 uppercase">Balance</th>
                            <th className="text-right px-4 py-3 font-display tracking-[0.2em] text-xs text-zinc-400 uppercase">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr><td colSpan={9} className="text-center text-zinc-500 py-12 font-display tracking-widest uppercase text-xs">Loading…</td></tr>
                        )}
                        {!loading && (report.rows || []).length === 0 && (
                            <tr><td colSpan={9} className="text-center text-zinc-500 py-12 font-display tracking-widest uppercase text-xs">No accepted ambassadors yet</td></tr>
                        )}
                        {!loading && (report.rows || []).map((r) => (
                            <tr key={r.ambassador_id} data-testid={`payout-row-${r.ambassador_id}`} className={`border-b border-white/5 ${r.balance_due > 0 ? "" : "opacity-70"}`}>
                                <td className="px-4 py-3">
                                    <div className="text-white font-display tracking-wider">{r.name}</div>
                                    <div className="text-xs text-zinc-500 font-mono">{r.email}</div>
                                    {r.payout_method && (
                                        <div className="text-[10px] font-display tracking-[0.2em] uppercase text-zinc-600 mt-0.5">
                                            {r.payout_method}
                                            {r.payout_handle && <span className="text-zinc-500 normal-case tracking-normal font-mono"> · {r.payout_handle}</span>}
                                        </div>
                                    )}
                                    {!r.payout_method && (
                                        <div className="text-[10px] font-display tracking-[0.2em] uppercase text-yellow-500/70 mt-0.5">
                                            ⚠ No payout info — DM them
                                        </div>
                                    )}
                                </td>
                                <td className="px-4 py-3 text-right">
                                    <span className="font-mono text-[#E63946]">
                                        {r.current_rank > 0 ? "★".repeat(r.current_rank) : "—"}
                                    </span>
                                </td>
                                <td className="px-4 py-3 text-right font-mono text-zinc-300">{r.commission_pct}%</td>
                                <td className="px-4 py-3 text-right font-mono text-zinc-300">{r.orders}</td>
                                <td className="px-4 py-3 text-right font-mono text-zinc-300">${r.gross_sales.toFixed(2)}</td>
                                <td className="px-4 py-3 text-right font-mono text-white">${r.commission_owed.toFixed(2)}</td>
                                <td className="px-4 py-3 text-right font-mono text-zinc-400">${r.paid_in_range.toFixed(2)}</td>
                                <td className={`px-4 py-3 text-right font-mono ${r.balance_due > 0 ? "text-[#E63946] font-bold" : "text-zinc-600"}`}>
                                    ${r.balance_due.toFixed(2)}
                                </td>
                                <td className="px-4 py-3 text-right">
                                    <button
                                        data-testid={`payout-mark-${r.ambassador_id}`}
                                        onClick={() => setModalRow(r)}
                                        disabled={r.commission_owed === 0}
                                        className="text-[10px] font-display tracking-[0.25em] uppercase px-3 py-1.5 border border-[#E63946] text-[#E63946] hover:bg-[#E63946] hover:text-white disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-[#E63946]"
                                    >
                                        Mark Paid
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Payout history */}
            <div className="bg-fgm-surface border border-white/10 p-5">
                <h3 className="font-display tracking-[0.3em] text-sm uppercase text-zinc-400 mb-3">Payout History ({history.length})</h3>
                {history.length === 0 ? (
                    <p className="text-xs text-zinc-500 italic">No payouts recorded yet. Mark one above and it'll log here.</p>
                ) : (
                    <div className="space-y-1.5">
                        {history.map((p) => (
                            <div key={p.id} data-testid={`payout-history-${p.id}`} className="flex items-center justify-between gap-2 text-sm border-b border-white/5 py-1.5">
                                <div className="flex-1 min-w-0">
                                    <span className="text-white font-display tracking-wider">{p.ambassador_name}</span>
                                    <span className="text-zinc-500 ml-2 font-mono text-xs">{p.ambassador_email}</span>
                                </div>
                                <span className="text-[#E63946] font-mono font-bold whitespace-nowrap">${p.amount.toFixed(2)}</span>
                                <span className="text-[10px] font-display tracking-[0.25em] uppercase text-zinc-400 whitespace-nowrap">{p.method}</span>
                                <span className="text-xs text-zinc-500 font-mono whitespace-nowrap hidden sm:inline">{p.period_start} → {p.period_end}</span>
                                {p.reference && <span className="text-xs text-zinc-500 font-mono truncate hidden md:inline" title={p.reference}>"{p.reference}"</span>}
                                <button
                                    data-testid={`payout-undo-${p.id}`}
                                    onClick={() => removePayout(p)}
                                    className="text-zinc-500 hover:text-[#E63946] text-lg leading-none px-1"
                                    aria-label="Undo"
                                    title="Undo this payout record"
                                >×</button>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <MarkPaidModal
                open={!!modalRow}
                row={modalRow}
                periodFrom={periodFrom}
                periodTo={periodTo}
                password={password}
                onClose={() => setModalRow(null)}
                onSaved={load}
            />
        </div>
    );
}
